import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import { Section, SectionHeader } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { usePersonalization } from "@/lib/personalization";
import { trackEvent } from "@/lib/telemetry";

/**
 * Recommendations for the visitor's chosen goal or role.
 *
 * Renders only after an explicit selection. Every recommendation is a public
 * page also reachable from the navigation, and the demo CTA always points at
 * the same public request form regardless of wording.
 */
export const PersonalizedGuide = () => {
  const { profile, selection, clear } = usePersonalization();
  if (!profile || !selection) return null;

  return (
    <Section tone="mist" aria-labelledby="personalized-heading">
      <SectionHeader
        eyebrow={selection.mode === "goal" ? "Suggested for your focus" : "Suggested for your role"}
        title={<span id="personalized-heading">Recommended starting points for {profile.label.toLowerCase()}</span>}
        description="Suggestions based on the option you selected. Nothing here is restricted — every page is also available from the site navigation."
      />

      <ul className="mt-8 grid gap-4 md:grid-cols-3">
        {profile.recommendations.map((rec) => (
          <li key={rec.href + rec.label}>
            <Link
              to={rec.href}
              onClick={() => trackEvent("nav_click", rec.href, { surface: "personalized_guide" })}
              className="ds-card group flex h-full flex-col gap-2 p-5 transition-colors hover:border-primary/50"
            >
              <span className="flex items-center gap-2 ds-h6 text-foreground">
                <Compass className="size-4 text-primary" aria-hidden />
                {rec.label}
              </span>
              <span className="ds-caption text-muted-foreground">{rec.note}</span>
              <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-semibold text-primary">
                Open
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Button asChild>
          <Link
            to="/request-demo"
            onClick={() => trackEvent("demo_cta_click", "personalized_guide", { surface: "personalized_guide" })}
          >
            {profile.cta.label}
          </Link>
        </Button>
        <p className="ds-caption text-muted-foreground">{profile.cta.helper}</p>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Link to="/solutions" className="ds-caption font-semibold text-primary underline">
          Browse every solution
        </Link>
        <Link to="/resources" className="ds-caption font-semibold text-primary underline">
          Browse the full resource library
        </Link>
        <Link to="/evidence/regulatory-quality" className="ds-caption font-semibold text-primary underline">
          Regulatory and quality
        </Link>
        <button type="button" onClick={clear} className="ds-caption text-muted-foreground underline">
          Show the standard homepage instead
        </button>
      </div>

      <p className="ds-legal mt-6 text-muted-foreground">
        AiMediQ™ powered by datasyteAI™ is in the pre-market development and validation phase. It is
        not FDA cleared or approved and is not available for clinical use or sale. Personalization
        changes the order of content and these suggestions only; it does not change any claim,
        capability status, or regulatory notice.
      </p>
    </Section>
  );
};
